/**
 * Ruʾya (رؤية) — "Vision" — the always-on top-right widget showing live time,
 * date, IP-derived city + country, and currency. Tap it to expand and reveal
 * a multi-currency price ticker (FX from exchangerate.host).
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Eye, MapPin, Globe2 } from 'lucide-react';
import api from '@/lib/api';

function useNow(tz) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const fmt = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(undefined, {
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
        timeZone: tz || undefined,
      });
    } catch { return null; }
  }, [tz]);
  const dateFmt = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(undefined, {
        weekday: 'short', day: '2-digit', month: 'short', timeZone: tz || undefined,
      });
    } catch { return null; }
  }, [tz]);
  return {
    time: fmt ? fmt.format(now) : now.toLocaleTimeString(),
    date: dateFmt ? dateFmt.format(now) : now.toDateString(),
  };
}

export default function RuyaWidget({ compact = false }) {
  const [geo, setGeo] = useState(null);
  const [rates, setRates] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/geo/me');
        setGeo(data);
      } catch {
        setGeo({ city: '—', country: '—', currency: 'USD', timezone: 'UTC' });
      }
    })();
  }, []);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get('/fx/rates', { params: { base: geo?.currency || 'QAR' } });
        setRates(data);
      } catch { /* silent */ }
    })();
  }, [open, geo?.currency]);

  const { time, date } = useNow(geo?.timezone);

  if (!geo) {
    // Skeleton — keeps the layout from jumping
    return (
      <div className="hidden md:flex items-center gap-2 h-9 px-3 rounded-md border border-slate-200/70 dark:border-white/10 bg-white/40 dark:bg-white/5 animate-pulse">
        <Eye size={13} className="text-slate-400 dark:text-white/40"/>
        <span className="font-mono text-[11px] text-slate-400 dark:text-white/40">— : — : —</span>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        data-testid="ruya-widget"
        onClick={() => setOpen((v) => !v)}
        className={`group hidden md:flex items-center gap-2.5 h-9 px-3 rounded-md border border-slate-200/70 dark:border-white/10
                    bg-white/70 dark:bg-white/[0.03] hover:border-[#8B3FC5] transition-colors ${compact ? '' : ''}`}
        title="Ruʾya — your bird's-eye view"
      >
        <Eye size={13} className="text-[#5E1F8B]" />
        <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#5E1F8B]">Ruʾya</span>
        <span className="hidden lg:inline font-mono text-[11px] tabular-nums text-slate-700 dark:text-white/80">
          {time}
        </span>
        <span className="hidden xl:inline text-[11px] text-slate-500 dark:text-white/60">
          · {geo.city || geo.country}
        </span>
      </button>

      {open && (
        <div
          data-testid="ruya-panel"
          className="absolute right-0 mt-2 w-[340px] rounded-lg border border-slate-200 dark:border-white/10
                     bg-white dark:bg-[#0d0d0d] shadow-2xl p-5 z-50"
          onMouseLeave={() => setOpen(false)}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#5E1F8B] font-bold">رؤية · Ruʾya</div>
              <div className="display text-xl font-bold mt-0.5 text-slate-900 dark:text-white">
                Your bird&apos;s-eye view
              </div>
            </div>
            <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#8B3FC5] to-[#5E1F8B] flex items-center justify-center">
              <Eye size={15} className="text-black"/>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-md border border-slate-200 dark:border-white/10 p-3">
              <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 dark:text-white/50 font-bold">Local time</div>
              <div className="font-mono text-lg tabular-nums mt-1 text-slate-900 dark:text-white">{time}</div>
              <div className="text-[10px] text-slate-500 dark:text-white/50 mt-0.5">{date}</div>
            </div>
            <div className="rounded-md border border-slate-200 dark:border-white/10 p-3">
              <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 dark:text-white/50 font-bold">Location</div>
              <div className="text-sm font-medium mt-1 text-slate-900 dark:text-white flex items-center gap-1">
                <MapPin size={11} className="text-[#5E1F8B]"/>
                {geo.city || '—'}
              </div>
              <div className="text-[10px] text-slate-500 dark:text-white/50 mt-0.5 flex items-center gap-1">
                <Globe2 size={9}/>
                {geo.country} · {geo.timezone}
              </div>
            </div>
          </div>

          <div className="mt-4">
            <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 dark:text-white/50 font-bold">
              Currency rates · base {rates?.base || (geo.currency || 'QAR')}
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {(rates ? Object.entries(rates.rates).slice(0, 9) : []).map(([k, v]) => (
                <div key={k} className="rounded border border-slate-100 dark:border-white/5 px-2 py-1.5">
                  <div className="text-[9px] uppercase text-slate-500 dark:text-white/50">{k}</div>
                  <div className="font-mono text-[11px] tabular-nums text-slate-900 dark:text-white">{Number(v).toFixed(k === 'JPY' || k === 'INR' || k === 'TRY' || k === 'EGP' ? 2 : 4)}</div>
                </div>
              ))}
              {!rates && (
                <div className="col-span-3 text-[10px] text-slate-400 italic">Loading rates…</div>
              )}
            </div>
          </div>

          <div className="mt-4 text-[10px] text-slate-400 dark:text-white/40 leading-relaxed">
            رؤية (Ruʾya) — Arabic for &ldquo;vision&rdquo;. A panoramic glance at where you are, when you are, and how the world is priced.
          </div>
        </div>
      )}
    </div>
  );
}
